// components/modals/MeetingEventFormModal.js

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import { FiUpload, FiTrash2, FiLoader, FiFileText, FiX } from 'react-icons/fi';
import * as meetingsApi from '../../services/meetingsApi';
const modalRoot = document.getElementById('modal-root');

const MeetingEventFormModal = ({ isOpen, onClose, onSave, meetingId, event, accessToken }) => {
    const [newVttFile, setNewVttFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const vttInputRef = useRef(null);

    
    // Overflow lock
    useEffect(() => {
        if (isOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = 'unset';
        return () => document.body.style.overflow = 'unset';
    }, [isOpen]);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.vtt')) {
            setNewVttFile(file);
        } else if (file) {
            toast.error("Invalid file type. Please upload a .vtt file.");
            e.target.value = null;
        }
    };

    const handleAddTranscript = async () => {
        if (!newVttFile) {
            toast.error("Please select a .vtt file to upload.");
            return;
        }
        setIsUploading(true);
        try {
            // +++ Use the real API +++
            const updatedMeeting = await meetingsApi.addTranscript(accessToken, meetingId, event.id, newVttFile);
            onSave(updatedMeeting); // Pass the whole updated meeting up
            
            toast.success("Transcript added, event is reprocessing.");
            setNewVttFile(null);
            if (vttInputRef.current) vttInputRef.current.value = null;
        } catch (err) {
            toast.error(`Failed to add transcript: ${err.message}`);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteTranscript = async (transcriptId) => {
        if (!window.confirm("Are you sure you want to delete this transcript?")) return;
        
        try {
            // +++ Use the real API +++
            const updatedMeeting = await meetingsApi.deleteTranscript(accessToken, meetingId, event.id, transcriptId);
            onSave(updatedMeeting); // Pass updated meeting up
            toast.success("Transcript deleted.");
        } catch (err) {
            toast.error(`Failed to delete transcript: ${err.message}`);
        }
    };
    
    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm z-[60] flex justify-center items-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-zinc-900 text-p-text rounded-2xl shadow-xl relative flex flex-col w-full max-h-[90vh] max-w-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center p-4 md:p-6 border-b border-slate-700">
                    <div>
                        <h3 className="text-xl font-semibold text-white">Manage Event Transcripts</h3>
                        <p className="text-sm text-blue-300">Event Date: {new Date(event.date).toLocaleDateString()}</p>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-full text-gray-400 hover:bg-slate-700 hover:text-white">
                        <FiX size={24} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-4 md:p-6 overflow-y-auto space-y-4">
                    {/* Add New Transcript Form */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 space-y-3">
                        <label className="text-xs font-medium text-slate-400">Add New VTT Transcript</label>
                        <input
                            type="file"
                            ref={vttInputRef}
                            accept=".vtt"
                            onChange={handleFileChange}
                            className="w-full text-sm text-slate-300
                                file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0
                                file:text-xs file:font-semibold file:bg-slate-700 file:text-slate-300
                                hover:file:bg-slate-600"
                        />
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={handleAddTranscript}
                                disabled={!newVttFile || isUploading}
                                className="flex items-center justify-center gap-2 text-xs w-36 h-8 px-4 py-2 rounded-md bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors disabled:bg-slate-600"
                            >
                                {isUploading ? <FiLoader className="animate-spin" /> : <><FiUpload size={14} /> Add Transcript</>}
                            </button>
                        </div>
                    </div>

                    {/* Existing Transcripts List */}
                    <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-slate-300">Existing Transcripts ({event.transcripts.length})</h4>
                        {event.transcripts.length > 0 ? (
                            event.transcripts.map(t => (
                                <div key={t.id} className="flex items-center justify-between bg-slate-800 p-2 rounded-md">
                                    <div className="flex items-center gap-3">
                                        <FiFileText className="text-blue-400" />
                                        <p className="text-sm text-slate-200">{t.filename}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteTranscript(t.id)}
                                        title="Delete Transcript"
                                        className="p-1 text-slate-400 hover:text-red-500 rounded-full hover:bg-slate-700"
                                    >
                                        <FiTrash2 size={16} />
                                    </button>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-slate-500 text-center py-2">No transcripts for this event.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        modalRoot
    );
};

export default MeetingEventFormModal;